import { useCallback, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useAutoStartTour } from '@/features/guided-help';
import type { GuideTourId } from '@/features/guided-help/content/tours';
import { useWizard } from '../engine';
import { WizardStepper, WizardStepperCompact } from './WizardStepper';
import { WizardNavigation } from './WizardNavigation';
import { stepRegistry, NotFoundStep } from '../registry';

interface WizardContainerProps {
  className?: string;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  header?: ReactNode;
}

export function WizardContainer({
  className,
  onSubmit,
  isSubmitting = false,
  header,
}: WizardContainerProps) {
  const { currentStep, draft, updateStepData } = useWizard();

  // Every wizard tour is `wizard.` + the flow's step id, so the container that
  // already knows the current step owns the auto-start. Keeping it here rather
  // than inside each step component is what lets the steps be reused outside
  // the wizard (the details-page edit modals) without a flag to switch it off.
  useAutoStartTour(`wizard.${currentStep.id}` as GuideTourId);

  // Resolve the step component from the registry
  const StepComponent = stepRegistry.get(currentStep.componentKey) ?? NotFoundStep;

  const draftSliceKey = currentStep.draftSliceKey;
  const sliceData = draftSliceKey != null ? draft[draftSliceKey] : null;

  const handleUpdateData = useCallback(
    (data: Record<string, unknown>) => {
      if (draftSliceKey == null) return;
      updateStepData(
        draftSliceKey,
        data as Parameters<typeof updateStepData>[1],
      );
    },
    [draftSliceKey, updateStepData],
  );

  return (
    <div
      className={cn(
        'flex min-h-screen flex-col bg-gray-50',
        className
      )}
    >
      {/* Header (optional) */}
      {header && <div className="border-b border-gray-200 bg-white">{header}</div>}

      {/* Stepper - Desktop: wide width when space available (max-w-7xl) so titles fit or wrap to 2 lines */}
      <div className="hidden border-b border-gray-200 bg-white px-4 py-3 md:block">
        <div className="mx-auto w-full max-w-7xl">
          <WizardStepper />
        </div>
      </div>

      {/* Stepper - Mobile */}
      <div className="border-b border-gray-200 bg-white px-4 py-3 md:hidden">
        <WizardStepperCompact />
      </div>

      {/* Step content */}
      <div className="flex-1 overflow-auto pb-24">
        <div className="mx-auto max-w-4xl px-4 py-6">
          {/* Step title and description */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">
              {currentStep.title}
            </h1>
            {currentStep.description && (
              <p className="mt-1 text-gray-600">{currentStep.description}</p>
            )}
          </div>

          {/* Step component */}
          <StepComponent
            data={sliceData}
            updateData={handleUpdateData}
            draft={draft}
          />
        </div>
      </div>

      {/* Floating Navigation - Fixed to bottom right */}
      <WizardNavigation onSubmit={onSubmit} isSubmitting={isSubmitting} floating />
    </div>
  );
}

// ============================================================================
// CARD VARIANT
// ============================================================================

interface WizardContainerCardProps {
  className?: string;
  onSubmit?: () => void;
  isSubmitting?: boolean;
}

/**
 * Card-style container with the wizard in a centered card.
 * Better for simpler forms or when embedded in a larger page.
 */
export function WizardContainerCard({
  className,
  onSubmit,
  isSubmitting = false,
}: WizardContainerCardProps) {
  const { currentStep, draft, updateStepData } = useWizard();

  const StepComponent = stepRegistry.get(currentStep.componentKey) ?? NotFoundStep;
  const draftSliceKey = currentStep.draftSliceKey;
  const sliceData = draftSliceKey != null ? draft[draftSliceKey] : null;

  const handleUpdateData = useCallback(
    (data: Record<string, unknown>) => {
      if (draftSliceKey == null) return;
      updateStepData(
        draftSliceKey,
        data as Parameters<typeof updateStepData>[1],
      );
    },
    [draftSliceKey, updateStepData],
  );

  return (
    <div
      className={cn(
        'flex min-h-screen items-center justify-center bg-gray-100 p-4',
        className
      )}
    >
      <div className="w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-xl">
        {/* Stepper */}
        <div className="border-b border-gray-200 px-6 py-4">
          <WizardStepper />
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {currentStep.title}
            </h2>
            {currentStep.description && (
              <p className="mt-1 text-sm text-gray-600">
                {currentStep.description}
              </p>
            )}
          </div>

          <StepComponent
            data={sliceData}
            updateData={handleUpdateData}
            draft={draft}
          />
        </div>

        {/* Navigation */}
        <WizardNavigation onSubmit={onSubmit} isSubmitting={isSubmitting} />
      </div>
    </div>
  );
}
